import { message } from 'antd';
import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import i18n from '@/i18n/config';
import { API_CONFIG, validateApiConfig } from './apiConfig';

type TokenCallback = () => string | null;
type SigninCallback = () => void;

let getTokenCallback: TokenCallback | null = null;
let signinCallback: SigninCallback | null = null;

export const setTokenCallback = (callback: TokenCallback): void => {
  getTokenCallback = callback;
};

export const setSigninCallback = (callback: SigninCallback): void => {
  signinCallback = callback;
};

export const getAccessToken = (): string | null => {
  if (getTokenCallback) {
    return getTokenCallback();
  }
  return null;
};

const triggerSignin = (): void => {
  if (signinCallback) {
    signinCallback();
  } else {
    console.warn('Signin callback not set, redirecting to login page');
    if (typeof window !== 'undefined') {
      window.location.href = '/login';
    }
  }
};

const apiClient = axios.create({
  baseURL: API_CONFIG.FULL_API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: API_CONFIG.TIMEOUTS.CLIENT,
});

if (!validateApiConfig()) {
  console.error('API Client initialization failed: configuration validation failed');
}

apiClient.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    try {
      const token = getAccessToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    } catch (error) {
      console.error('Failed to get access token for request interceptor:', error);
      return Promise.reject(new Error('Could not retrieve access token for authentication.'));
    }
  },
  (error) => {
    console.error('Request setup error:', error);
    return Promise.reject(error);
  }
);

apiClient.interceptors.response.use(
  (response) => {
    return response;
  },
  (error: AxiosError) => {
    if (error.response) {
      const { status, data } = error.response;
      const errorData = data as { message?: string };
      const errorMessage = errorData?.message || i18n.t('common:api.unknownError');

      switch (status) {
        case 401:
          if (typeof window !== 'undefined') {
            const currentPath = window.location.pathname;
            message.warning(i18n.t('common:api.sessionExpired'));

            triggerSignin();

            const callbackUrl = encodeURIComponent(currentPath);
            window.location.href = `/login?callbackUrl=${callbackUrl}`;
          }

          return new Promise(() => { });

        case 403:
          message.error(i18n.t('common:api.forbidden'));
          break;
        case 404:
          message.error(i18n.t('common:api.notFound'));
          break;
        case 500:
          message.error(i18n.t('common:api.serverError', { errorMessage }));
          break;
        default:
          message.error(i18n.t('common:api.httpError', { status, errorMessage }));
      }
    } else if (error.request) {
      message.error(i18n.t('common:api.networkError'));
    } else {
      message.error(i18n.t('common:api.requestFailed', { message: error.message }));
    }

    return Promise.reject(error);
  }
);

export default apiClient;
